import { LitElement, html, css, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { tokensStyles } from './styles/tokens.js';

/**
 * Negative-feedback inline reason card (P3-025a).
 *
 * **Architectural note.** The original `P3-025` BACKLOG row called this a
 * negative-feedback **modal**. The Dev-Handoff Figma frames make clear it
 * is **not** a modal — there is no scrim, no `inert`-the-rest-of-the-widget,
 * no overlay. It is an inline bordered card that grows the conversation
 * column downward, the same way an assistant bubble does. Treating it as
 * a modal would conflict with the design intent (the user can keep reading
 * the conversation while choosing reasons) and with the disclaimer-modal
 * / close-confirmation modal patterns we already ship. The BACKLOG row
 * has been renamed accordingly.
 *
 * Source frames (Dev-Handoff file `DSMVDudtzt2Haob8uI37xQ`):
 *  - `Chatbot_neg feedback_followup` (`5642:4606`) — empty state.
 *  - `Chatbot_neg feedback_followup filled` (`5642:4692`) — at least one
 *    box checked, Submit enabled.
 *  - `Chatbot_chat end_neg feeback` (`5642:3754` / `5642:3849`) — same
 *    primitive reused with end-of-chat title copy.
 *
 * **Mount logic is NOT this primitive's responsibility.** The card is a
 * standalone presentational/event-emitting component. The per-turn flow
 * (P3-023b) and the end-of-chat flow (P3-024a) are responsible for
 * mounting it inline beneath the appropriate feedback row, listening
 * for the `mwi-assistant-feedback-reasons` / `mwi-assistant-feedback-dismiss`
 * events, and unmounting the card after dispatch. This split mirrors
 * the P1-058a/b/c primitive-vs-mounter pattern.
 *
 * Wire shape: the `reasons` array in the dispatched `CustomEvent` detail
 * is in **render** order (`inaccurate, irrelevant, repetitive, unhelpful`)
 * NOT click order, so the API payload is stable regardless of the order
 * the user toggles the checkboxes.
 *
 * See `planning/figma/TOKENS.md` §"Negative feedback reason card (P3-025a)"
 * for the full design contract (anatomy, tokens, behavioural notes).
 */

/**
 * Default title copy for the per-turn negative-feedback flow (P3-023b).
 * The end-of-chat caller (P3-024a) overrides this via the `title-copy`
 * attribute with the longer "did not meet your expectations" wording —
 * see `planning/figma/TOKENS.md` §"Negative feedback reason card" for
 * the full per-site copy table.
 */
const DEFAULT_TITLE_COPY =
  'We are sorry that we missed the mark. Please select from the ' +
  'following options to help us understand the AI response did ' +
  'not meet your needs.';

/**
 * Custom event dispatched when the user clicks Submit with at least one
 * checkbox checked. Bubbles + composed so a host listener (P3-023b /
 * P3-024a) can subscribe at any level above the shadow root.
 *
 * `detail.reasons` is in **render order** — see the class JSDoc.
 */
export const FEEDBACK_REASONS_EVENT = 'mwi-assistant-feedback-reasons';

/**
 * Custom event dispatched when the user clicks Dismiss, or presses Esc
 * while focus is anywhere inside the card. Bubbles + composed.
 *
 * The caller is responsible for unmounting the card; the thumb-down
 * state on the per-turn row above is preserved (the user is on record
 * as having said "not helpful", they declined to elaborate).
 */
export const FEEDBACK_DISMISS_EVENT = 'mwi-assistant-feedback-dismiss';

/**
 * Detail shape for the {@link FEEDBACK_REASONS_EVENT} CustomEvent.
 */
export interface MwiAssistantFeedbackReasonsEventDetail {
  /** Caller-supplied correlation id (per-turn id, end-of-chat id). May be `null`. */
  turnId: string | null;
  /** Selected reason values, in render order — `inaccurate, irrelevant, repetitive, unhelpful`. */
  reasons: readonly string[];
}

/**
 * Detail shape for the {@link FEEDBACK_DISMISS_EVENT} CustomEvent.
 */
export interface MwiAssistantFeedbackDismissEventDetail {
  /** Caller-supplied correlation id. May be `null`. */
  turnId: string | null;
}

/**
 * Canonical reason values, in render order. The wire payload (`reasons[]`
 * on the future `POST /api/feedback` body, P3-022) uses these strings
 * verbatim. Adding a new reason requires bumping the API contract; do
 * not reorder existing entries.
 */
const REASON_RENDER_ORDER = [
  'inaccurate',
  'irrelevant',
  'repetitive',
  'unhelpful',
] as const;

type ReasonValue = (typeof REASON_RENDER_ORDER)[number];

/**
 * User-facing label per reason value. Copy verified 2026-04-29 against
 * `Chatbot_neg feedback_followup filled` (`5642:4692`).
 */
const REASON_LABELS: Readonly<Record<ReasonValue, string>> = {
  inaccurate: 'It was inaccurate',
  irrelevant: 'It was irrelevant',
  repetitive: 'It was repetitive',
  unhelpful: 'It was unhelpful',
};

/**
 * Inline `check-1` SVG (Lucide `check` is a permissive free analog of the
 * CDS `check-1` icon referenced by the Figma frame). Rendered at 10.5 ×
 * 10.5 px with `currentColor` stroke so the parent's color cascades.
 * Visibility is gated by the `:checked` selector in the component's CSS.
 */
const CHECK_ICON_SVG = html`
  <svg
    width="10.5"
    height="10.5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="3"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
`;

/**
 * Stable id for the title `<p>` element. The form's `aria-labelledby`
 * points at it. Hardcoded (not generated) because the card always
 * renders exactly one title and one form per instance, the shadow root
 * keeps the id local, and a stable id makes debugging easier.
 */
const TITLE_ID = 'mwi-feedback-reason-card-title';

@customElement('mwi-feedback-reason-card')
export class MwiFeedbackReasonCardElement extends LitElement {
  static override styles = [
    tokensStyles,
    css`
      :host {
        display: block;
        font-family: var(--mwi-assistant-font-family);
        color: var(--mwi-assistant-color-text-primary);
      }

      [part='feedback-reason-card'] {
        display: flex;
        flex-direction: column;
        gap: var(--mwi-assistant-space-4);
        background: var(--mwi-assistant-color-bg-body);
        border: var(--mwi-assistant-border-thin) solid
          var(--mwi-assistant-color-border-card);
        border-radius: var(--mwi-assistant-radius-md);
        padding: var(--mwi-assistant-space-4);
        margin: 0;
      }

      [part='feedback-reason-title'] {
        font-size: var(--mwi-assistant-font-body-sm-size);
        line-height: var(--mwi-assistant-font-body-sm-line);
        font-weight: var(--mwi-assistant-font-body-sm-weight);
        color: var(--mwi-assistant-color-text-primary);
        margin: 0;
      }

      [part='feedback-reason-options'] {
        display: flex;
        flex-direction: column;
        gap: var(--mwi-assistant-space-2);
      }

      [part='feedback-reason-checkbox'] {
        display: inline-flex;
        align-items: center;
        gap: var(--mwi-assistant-space-2);
        cursor: pointer;
        font-size: var(--mwi-assistant-font-body-sm-size);
        line-height: var(--mwi-assistant-font-body-sm-line);
        font-weight: var(--mwi-assistant-font-body-sm-weight);
        color: var(--mwi-assistant-color-text-primary);
      }

      /* Visually-hidden native checkbox: kept in tab order so the row is
         fully keyboard-accessible. NOT \`display: none\` — that would
         remove it from the tab sequence and break Space-to-toggle.
         Standard "visually hidden but focusable" pattern. */
      input[type='checkbox'] {
        position: absolute;
        width: 1px;
        height: 1px;
        clip: rect(0 0 0 0);
        clip-path: inset(50%);
        white-space: nowrap;
        overflow: hidden;
        margin: 0;
      }

      [part='feedback-reason-box'] {
        width: 20px;
        height: 20px;
        border: var(--mwi-assistant-border-thin) solid
          var(--mwi-assistant-color-interactive-secondary);
        border-radius: var(--mwi-assistant-radius-sm);
        background: var(--mwi-assistant-color-bg-input);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: var(--mwi-assistant-color-text-on-primary);
        flex-shrink: 0;
        box-sizing: border-box;
      }

      input[type='checkbox']:checked + [part='feedback-reason-box'] {
        background: var(--mwi-assistant-color-interactive-primary);
        border-color: var(--mwi-assistant-color-interactive-primary);
      }

      input[type='checkbox']:checked + [part='feedback-reason-box'] svg {
        display: block;
      }

      input[type='checkbox']:not(:checked) + [part='feedback-reason-box'] svg {
        display: none;
      }

      input[type='checkbox']:focus-visible + [part='feedback-reason-box'] {
        outline: 2px solid var(--mwi-assistant-color-interactive-primary);
        outline-offset: 2px;
      }

      [part='feedback-reason-actions'] {
        display: flex;
        align-items: center;
        gap: var(--mwi-assistant-space-6);
      }

      [part='feedback-reason-dismiss'] {
        background: transparent;
        border: var(--mwi-assistant-border-thick) solid
          var(--mwi-assistant-color-border-card);
        border-radius: var(--mwi-assistant-radius-sm);
        padding: 6px 16px;
        height: var(--mwi-assistant-size-button-height);
        font-family: var(--mwi-assistant-font-family);
        font-size: var(--mwi-assistant-font-button-sm-size);
        line-height: var(--mwi-assistant-font-button-sm-line);
        font-weight: var(--mwi-assistant-font-button-sm-weight);
        color: var(--mwi-assistant-color-interactive-primary);
        cursor: pointer;
      }

      [part='feedback-reason-dismiss']:focus-visible {
        outline: 2px solid var(--mwi-assistant-color-interactive-primary);
        outline-offset: 2px;
      }

      [part='feedback-reason-submit'] {
        background: var(--mwi-assistant-color-interactive-primary);
        border: none;
        border-radius: var(--mwi-assistant-radius-sm);
        padding: 6px 16px;
        height: var(--mwi-assistant-size-button-height);
        font-family: var(--mwi-assistant-font-family);
        font-size: var(--mwi-assistant-font-button-sm-size);
        line-height: var(--mwi-assistant-font-button-sm-line);
        font-weight: var(--mwi-assistant-font-button-sm-weight);
        color: var(--mwi-assistant-color-text-on-primary);
        cursor: pointer;
      }

      [part='feedback-reason-submit']:focus-visible {
        outline: 2px solid var(--mwi-assistant-color-interactive-primary);
        outline-offset: 2px;
      }

      [part='feedback-reason-submit']:disabled {
        background: var(--mwi-assistant-color-interactive-disabled);
        cursor: not-allowed;
      }
    `,
  ];

  /**
   * User-facing title copy. Configurable per use site so the same
   * primitive can render both the per-turn variant ("did not meet
   * your needs") and the end-of-chat variant ("did not meet your
   * expectations") without code duplication.
   */
  @property({ attribute: 'title-copy' })
  titleCopy: string = DEFAULT_TITLE_COPY;

  /**
   * Caller-supplied correlation id, forwarded into the dispatched event
   * `detail` so a host listener (P3-023b per-turn / P3-024a end-of-chat)
   * can match the emission back to the right transcript turn or session
   * without keeping its own bookkeeping.
   */
  @property({ attribute: 'turn-id' })
  turnId: string | null = null;

  /**
   * Set of reason values (the `inaccurate / irrelevant / repetitive /
   * unhelpful` discriminator strings) currently checked. Backed by a
   * `Set` rather than booleans on each reason because the render-time
   * lookup is `O(1)` per reason and the Submit-enabled gate just needs
   * `.size > 0` — no bookkeeping per row required. Triggers a re-render
   * via `requestUpdate()` on every change because mutating a Set in
   * place doesn't trip Lit's reactivity.
   */
  @state()
  private _checkedReasons = new Set<ReasonValue>();

  override render(): TemplateResult {
    const submitDisabled = this._checkedReasons.size === 0;
    return html`
      <form
        part="feedback-reason-card"
        role="group"
        aria-labelledby=${TITLE_ID}
        @submit=${this._onSubmit}
        @keydown=${this._onKeydown}
      >
        <p part="feedback-reason-title" id=${TITLE_ID}>${this.titleCopy}</p>
        <div part="feedback-reason-options">
          ${REASON_RENDER_ORDER.map((value) =>
            this._renderCheckbox(value, REASON_LABELS[value])
          )}
        </div>
        <div part="feedback-reason-actions">
          <button
            type="button"
            part="feedback-reason-dismiss"
            @click=${this._onDismiss}
          >
            Dismiss
          </button>
          <button
            type="submit"
            part="feedback-reason-submit"
            ?disabled=${submitDisabled}
          >
            Submit
          </button>
        </div>
      </form>
    `;
  }

  /**
   * Renders one reason row: `<label>` → visually-hidden `<input
   * type="checkbox">` + visible styled box (the sibling `<span>` that
   * the `:checked` selector targets) + label text. The `<input>` stays
   * in the tab order; Space toggles it natively.
   */
  private _renderCheckbox(value: ReasonValue, label: string): TemplateResult {
    return html`
      <label part="feedback-reason-checkbox">
        <input
          type="checkbox"
          name="reasons"
          .value=${value}
          .checked=${this._checkedReasons.has(value)}
          @change=${this._onCheck}
        />
        <span part="feedback-reason-box" aria-hidden="true">
          ${CHECK_ICON_SVG}
        </span>
        <span part="feedback-reason-label">${label}</span>
      </label>
    `;
  }

  /**
   * Mirrors the checkbox's `checked` state into the internal Set and
   * re-renders. Driven by the native `change` event (NOT `click`) so
   * keyboard activation (Space) and pointer activation flow through
   * the same path — `change` is the canonical "value committed" signal
   * for form controls.
   */
  private _onCheck(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value as ReasonValue;
    if (input.checked) {
      this._checkedReasons.add(value);
    } else {
      this._checkedReasons.delete(value);
    }
    this.requestUpdate();
  }

  /**
   * Form submit handler. Always `preventDefault`s first thing so a
   * stray Enter on a checkbox doesn't navigate the page even if the
   * card is mounted directly under `<body>` (no enclosing widget
   * `<form>` to intercept).
   *
   * Defensive guard: if no boxes are checked, drops the submit on the
   * floor. The Submit button is `disabled` in that state — but a
   * programmatic `form.requestSubmit()` from a host script would
   * bypass the button. We treat that as a no-op rather than emit an
   * empty `reasons[]` because the wire contract (P3-022a) requires
   * at least one reason.
   *
   * Reasons are emitted in **render order** (`inaccurate, irrelevant,
   * repetitive, unhelpful`), NOT click order, so the wire shape is
   * stable across UI experiments.
   */
  private _onSubmit(event: Event): void {
    event.preventDefault();
    if (this._checkedReasons.size === 0) {
      return;
    }
    const reasons = REASON_RENDER_ORDER.filter((value) =>
      this._checkedReasons.has(value)
    );
    this.dispatchEvent(
      new CustomEvent<MwiAssistantFeedbackReasonsEventDetail>(
        FEEDBACK_REASONS_EVENT,
        {
          detail: { turnId: this.turnId, reasons },
          bubbles: true,
          composed: true,
        }
      )
    );
  }

  /**
   * Dismiss handler. Wired to both the Dismiss button click and Esc
   * (via {@link _onKeydown}). The caller is responsible for unmounting
   * the card; the thumb-down state on the per-turn feedback row above
   * is preserved (the user is on record as having said "not helpful",
   * they declined to elaborate).
   */
  private _onDismiss(): void {
    this.dispatchEvent(
      new CustomEvent<MwiAssistantFeedbackDismissEventDetail>(
        FEEDBACK_DISMISS_EVENT,
        {
          detail: { turnId: this.turnId },
          bubbles: true,
          composed: true,
        }
      )
    );
  }

  /**
   * Esc-key handler. Scoped to the form root (NOT `document`) so the
   * card never reaches outside its own tree — keeps it composable as a
   * primitive and avoids the "two cards on the page, Esc fires both"
   * bug. Mirrors the disclaimer / close-confirmation modal Esc
   * convention even though this is not a modal.
   */
  private _onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this._onDismiss();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mwi-feedback-reason-card': MwiFeedbackReasonCardElement;
  }
}
